import { toast } from 'react-toastify';
import React from 'react';
import greenCheck from '../assets/icons-home/green-check.svg';

// Custom toast component for bucket creation
interface BucketCreatedToastProps {
  closeToast?: () => void;
}

// Custom toast component for repository creation
interface RepoCreatedToastProps {
  closeToast?: () => void;
}

// Custom toast component for errors
interface ErrorToastProps {
  closeToast?: () => void;
  message: string;
}

// Generic success toast component
interface SuccessToastProps {
  closeToast?: () => void;
  message: string;
}

// Generic error toast with icon component
interface ErrorToastWithIconProps {
  closeToast?: () => void;
  message: string;
}

const BucketCreatedToast: React.FC<BucketCreatedToastProps> = ({ closeToast }) => {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '16px',
        width: '368px', // 400px - 2*16px padding
        minHeight: '82px', // 114px - 2*16px padding
        paddingLeft: '4px',
      }}
    >
      {/* Icon */}
      <img
        src={greenCheck}
        alt="Success"
        style={{
          width: '24px',
          height: '24px',
          flexShrink: 0,
          marginLeft: '4px',
        }}
      />

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontWeight: 600,
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0%',
            color: '#181D27',
            marginBottom: '4px',
          }}
        >
          Successfully created a bucket
        </div>
        <div
          style={{
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0%',
            color: '#535862',
          }}
        >
          Your bucket have been setup and your dashboard is live. You can now track activity.
        </div>
      </div>

      {/* Close button */}
      <button
        onClick={closeToast}
        style={{
          background: 'transparent',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          color: '#9CA3AF',
          fontSize: '18px',
          lineHeight: '18px',
          flexShrink: 0,
        }}
      >
        ✕
      </button>
    </div>
  );
};

const RepoCreatedToast: React.FC<RepoCreatedToastProps> = ({ closeToast }) => {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '16px',
        width: '368px', // 400px - 2*16px padding
        minHeight: '82px', // 114px - 2*16px padding
        paddingLeft: '4px',
      }}
    >
      {/* Icon */}
      <img
        src={greenCheck}
        alt="Success"
        style={{
          width: '24px',
          height: '24px',
          flexShrink: 0,
          marginLeft: '4px',
        }}
      />

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontWeight: 600,
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0%',
            color: '#181D27',
            marginBottom: '4px',
          }}
        >
          Successfully created a repository
        </div>
        <div
          style={{
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0%',
            color: '#535862',
          }}
        >
          Your repository has been setup and is ready to use. You can now manage your data.
        </div>
      </div>

      {/* Close button */}
      <button
        onClick={closeToast}
        style={{
          background: 'transparent',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          color: '#9CA3AF',
          fontSize: '18px',
          lineHeight: '18px',
          flexShrink: 0,
        }}
      >
        ✕
      </button>
    </div>
  );
};

const ErrorToast: React.FC<ErrorToastProps> = ({ closeToast, message }) => {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '16px',
        width: '368px', // 400px - 2*16px padding
        minHeight: '82px', // 114px - 2*16px padding
        paddingLeft: '12px',
      }}
    >
      {/* Content */}
      <div style={{ flex: 1, minWidth: 0, paddingRight: '8px' }}>
        <div
          style={{
            fontWeight: 600,
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0%',
            color: '#181D27',
            marginBottom: '4px',
          }}
        >
          Error
        </div>
        <div
          style={{
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0%',
            color: '#535862',
          }}
        >
          {message}
        </div>
      </div>

      {/* Close button */}
      <button
        onClick={closeToast}
        style={{
          background: 'transparent',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          color: '#9CA3AF',
          fontSize: '18px',
          lineHeight: '18px',
          flexShrink: 0,
        }}
      >
        ✕
      </button>
    </div>
  );
};

// Generic Success Toast with green check icon
const SuccessToast: React.FC<SuccessToastProps> = ({ closeToast, message }) => {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '16px',
        width: '368px',
        minHeight: '82px',
        paddingLeft: '4px',
      }}
    >
      {/* Icon */}
      <img
        src={greenCheck}
        alt="Success"
        style={{
          width: '24px',
          height: '24px',
          flexShrink: 0,
          marginLeft: '4px',
        }}
      />

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontWeight: 600,
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0%',
            color: '#181D27',
            marginBottom: '4px',
          }}
        >
          Success
        </div>
        <div
          style={{
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0%',
            color: '#535862',
          }}
        >
          {message}
        </div>
      </div>

      {/* Close button */}
      <button
        onClick={closeToast}
        style={{
          background: 'transparent',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          color: '#9CA3AF',
          fontSize: '18px',
          lineHeight: '18px',
          flexShrink: 0,
        }}
      >
        ✕
      </button>
    </div>
  );
};

// Error Toast with red icon
const ErrorToastWithIcon: React.FC<ErrorToastWithIconProps> = ({ closeToast, message }) => {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '16px',
        width: '368px',
        minHeight: '82px',
        paddingLeft: '4px',
      }}
    >
      {/* Error Icon - Red circle with X */}
      <div
        style={{
          width: '24px',
          height: '24px',
          borderRadius: '50%',
          backgroundColor: '#FEE2E2',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          marginLeft: '4px',
        }}
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M9 3L3 9M3 3L9 9"
            stroke="#DC2626"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontWeight: 600,
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0%',
            color: '#181D27',
            marginBottom: '4px',
          }}
        >
          Error
        </div>
        <div
          style={{
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0%',
            color: '#535862',
          }}
        >
          {message}
        </div>
      </div>

      {/* Close button */}
      <button
        onClick={closeToast}
        style={{
          background: 'transparent',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          color: '#9CA3AF',
          fontSize: '18px',
          lineHeight: '18px',
          flexShrink: 0,
        }}
      >
        ✕
      </button>
    </div>
  );
};

export const toastError = (message: string, autoCloseMs = 5000) => {
  toast(<ErrorToastWithIcon message={message} />, {
    position: 'top-right',
    autoClose: autoCloseMs,
    hideProgressBar: true,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    closeButton: false,
    className: 'custom-bucket-toast',
    style: {
      width: '400px',
      maxWidth: 'calc(100vw - 40px)',
      minHeight: '114px',
      borderRadius: '12px',
      border: '1px solid #D5D7DA',
      padding: '16px',
      backgroundColor: '#FFFFFF',
      boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
    },
  });
};

export const toastSuccess = (message: string, autoCloseMs = 5000) => {
  toast(<SuccessToast message={message} />, {
    position: 'top-right',
    autoClose: autoCloseMs,
    hideProgressBar: true,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    closeButton: false,
    className: 'custom-bucket-toast',
    style: {
      width: '400px',
      maxWidth: 'calc(100vw - 40px)',
      minHeight: '114px',
      borderRadius: '12px',
      border: '1px solid #D5D7DA',
      padding: '16px',
      backgroundColor: '#FFFFFF',
      boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
    },
  });
};

// Custom toast for bucket creation
export const toastBucketCreated = () => {
  toast(<BucketCreatedToast />, {
    position: 'top-right',
    autoClose: 5000,
    hideProgressBar: true,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    closeButton: false,
    className: 'custom-bucket-toast',
    style: {
      width: '400px',
      maxWidth: 'calc(100vw - 40px)',
      minHeight: '114px',
      borderRadius: '12px',
      border: '1px solid #D5D7DA',
      padding: '16px',
      backgroundColor: '#FFFFFF',
      boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
    },
  });
};

// Custom toast for repository creation
export const toastRepoCreated = () => {
  toast(<RepoCreatedToast />, {
    position: 'top-right',
    autoClose: 5000,
    hideProgressBar: true,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    closeButton: false,
    className: 'custom-bucket-toast',
    style: {
      width: '400px',
      maxWidth: 'calc(100vw - 40px)',
      minHeight: '114px',
      borderRadius: '12px',
      border: '1px solid #D5D7DA',
      padding: '16px',
      backgroundColor: '#FFFFFF',
      boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
    },
  });
};

// Custom toast for errors
export const toastErrorCustom = (message: string) => {
  toast(<ErrorToast message={message} />, {
    position: 'top-right',
    autoClose: 5000,
    hideProgressBar: true,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    closeButton: false,
    className: 'custom-bucket-toast',
    style: {
      width: '400px',
      maxWidth: 'calc(100vw - 40px)',
      minHeight: '114px',
      borderRadius: '12px',
      border: '1px solid #D5D7DA',
      padding: '16px',
      backgroundColor: '#FFFFFF',
      boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
    },
  });
};
